import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Upload, FileText, CheckCircle, X, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { tutorService } from '../services/tutorService';
import { toast } from 'sonner';

const DocumentUpload = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [documents, setDocuments] = useState({
    id_proof: null,
    certificate: null,
    resume: null
  });

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const response = await tutorService.getDocuments();
      setUploadedDocuments(response.data || []);
    } catch (error) {
      console.log('No documents yet');
    }
  };

  const handleFileUpload = async (type, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('document_type', type);

      const response = await tutorService.uploadDocument(formData);
      toast.success('Document uploaded successfully!');
      loadDocuments();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to upload document';
      toast.error(errorMessage);
      console.error('Upload error:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFile = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    setLoading(true);
    try {
      await tutorService.deleteDocument(documentId);
      toast.success('Document deleted successfully!');
      loadDocuments();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete document';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const hasIdProof = uploadedDocuments.some(doc => doc.document_type === 'id_proof');
    if (!hasIdProof) {
      toast.error('Please upload your Aadhaar / Identity Proof');
      return;
    }
    toast.success('Documents saved!');
    navigate('/tutor/dashboard');
  };

  const FileUploadBox = ({ type, label, required }) => {
    const inputId = `file-${type}`;
    const existingDoc = uploadedDocuments.find(doc => doc.document_type === type);

    return (
      <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-blue-400 transition-all duration-300 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="text-center">
          {existingDoc ? (
            <div className="space-y-4">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-6 w-6 text-blue-600" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                        {existingDoc.document_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {existingDoc.verification_status}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(existingDoc.id)}
                    disabled={loading}
                    className="text-red-500 hover:text-red-700 transition-colors duration-200 disabled:opacity-50"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById(inputId).click()}
                disabled={loading}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                Replace File
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full flex items-center justify-center mx-auto">
                {loading ? <Loader2 className="h-8 w-8 text-blue-600 animate-spin" /> : <Upload className="h-8 w-8 text-blue-600" />}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {label} {required && <span className="text-red-500">*</span>}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Click to upload
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById(inputId).click()}
                  disabled={loading}
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  Browse Files
                </Button>
              </div>
              <p className="text-xs text-gray-500">PDF, JPG, PNG (max 5MB)</p>
            </div>
          )}
          <input
            type="file"
            id={inputId}
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => handleFileUpload(type, e)}
            className="hidden"
            disabled={loading}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">

        {/* Document Upload Card */}
        <Card className="p-8 bg-white shadow-xl border-gray-100">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Upload Documents</h1>
            <p className="text-gray-600">
              Help us verify your credentials to ensure quality education for students
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Identity Proof */}
            <FileUploadBox
              type="id_proof"
              label="Aadhaar / Identity Proof"
              required={true}
            />

            {/* Degree Certificates */}
            <FileUploadBox
              type="certificate"
              label="Degree / Certificates (Optional)"
              required={false}
            />

            {/* Resume (Optional) */}
            <FileUploadBox
              type="resume"
              label="Resume (Optional)"
              required={false}
            />

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex space-x-3">
                <FileText className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-1">Document Guidelines:</p>
                  <ul className="space-y-1 text-blue-800">
                    <li>• All documents must be clear and readable</li>
                    <li>• Personal information should be visible</li>
                    <li>• Documents will be verified within 24-48 hours</li>
                    <li>• Your information is kept secure and confidential</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex space-x-4 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/tutor/dashboard')}
                className="flex-1 h-12 border-gray-300"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white h-12 shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
              >
                {loading ? 'Processing...' : 'Continue to Next Step'}
              </Button>
            </div>
          </form>
        </Card>
    </div>
  );
};

export default DocumentUpload;